import moment from "moment-timezone";
import {
  isSlotBlocked as originalIsSlotBlocked,
  isDayBlockedByDateRange,
} from "@/app/components/calendar/AvailabilityBlocks";
import { DEFAULT_MINIMUM_NOTICE_MINUTES } from "@/lib/constants/schedulingRules";

const convertFirestoreTimestamp = (timestamp) => {
  if (!timestamp) return null;

  let momentObj;

  if (timestamp._seconds !== undefined) {
    momentObj = moment.unix(timestamp._seconds).utc();
  } else if (timestamp.toDate && typeof timestamp.toDate === "function") {
    momentObj = moment(timestamp.toDate()).utc();
  } else {
    momentObj = moment(timestamp).utc();
  }

  return momentObj;
};

export const isDayBlocked = (
  day,
  schedulingRules,
  availability,
  meetingsByDate,
) => {
  if (!schedulingRules || !day) return false;

  const selectedDate = day.format("YYYY-MM-DD");

  // Rule 1: Daily Booking Limit Check
  if (schedulingRules.max_bookings?.enabled) {
    const maxBookingsPerDay = schedulingRules.max_bookings.per_day || 5;
    const meetingsOnDay = meetingsByDate[selectedDate] || [];

    if (meetingsOnDay.length >= maxBookingsPerDay) {
      return true;
    }
  }

  // Rule 2: Minimum Notice Check
  if (schedulingRules.minimum_notice?.enabled) {
    const minimumNoticeHours = schedulingRules.minimum_notice.hours || 24;
    const earliestAllowed = moment().add(minimumNoticeHours, "hours");

    if (day.isBefore(earliestAllowed, "day")) {
      return true;
    }
  }

  // Rule 3: Scheduling Window Check
  if (availability?.window) {
    const maxAllowed = moment().add(availability.window, "days");
    if (day.isAfter(maxAllowed, "day")) {
      return true;
    }
  }

  return false;
};

export const isSlotBlockedByRules = (
  slotStart,
  slotEnd,
  schedulingRules,
  existingMeetings,
  availability,
) => {
  if (!schedulingRules) return false;

  // Rule 1: Minimum Notice Check (slot-level)
  const minimumNoticeHours = schedulingRules.minimum_notice?.enabled
    ? schedulingRules.minimum_notice.hours || 24
    : null;
  const earliestAllowed =
    minimumNoticeHours !== null
      ? moment().add(minimumNoticeHours, "hours")
      : moment().add(DEFAULT_MINIMUM_NOTICE_MINUTES, "minutes");

  if (slotStart.isBefore(earliestAllowed)) {
    return true;
  }

  // Rule 2: Scheduling Window Check (slot-level)
  if (availability?.window) {
    const maxAllowed = moment().add(availability.window, "days");
    if (slotStart.isAfter(maxAllowed)) {
      return true;
    }
  }

  // Rule 3: Buffer Time Check
  if (schedulingRules.buffer_time?.enabled && existingMeetings.length > 0) {
    const bufferHours = schedulingRules.buffer_time.hours || 1;
    const bufferStart = slotStart.clone().subtract(bufferHours, "hours");
    const bufferEnd = slotEnd.clone().add(bufferHours, "hours");

    const hasBufferConflict = existingMeetings.some((meeting) => {
      const meetingStart = convertFirestoreTimestamp(
        meeting.scheduled_date_time,
      );
      if (!meetingStart) return false;

      const meetingEndTime = meetingStart
        .clone()
        .add(parseInt(meeting.duration || 60), "minutes");

      return (
        bufferStart.isBefore(meetingEndTime) && bufferEnd.isAfter(meetingStart)
      );
    });

    if (hasBufferConflict) {
      return true;
    }
  }

  return false;
};

export const checkDayAvailability = (
  day,
  availability,
  duration,
  userTimezone,
  schedulingRules,
  existingMeetings,
  meetingsByDate,
) => {
  if (!availability?.times || !duration) return false;

  const dayName = day.format("ddd");
  const dayAvailability = availability.times[dayName];

  if (!dayAvailability || dayAvailability.length === 0) return false;

  if (isDayBlockedByDateRange(day, availability.blocked_periods || [])) {
    return false;
  }

  if (isDayBlocked(day, schedulingRules, availability, meetingsByDate)) {
    return false;
  }

  const meetingDuration = parseInt(duration);

  for (const { start, end } of dayAvailability) {
    if (!start || !end) continue;

    const startTimeUTC = convertFirestoreTimestamp(start);
    const endTimeUTC = convertFirestoreTimestamp(end);

    if (!startTimeUTC || !endTimeUTC) continue;

    const startTimeInUserTZ = startTimeUTC.clone().tz(userTimezone);
    const endTimeInUserTZ = endTimeUTC.clone().tz(userTimezone);

    const selectedDate = day.clone().tz(userTimezone);
    const dayStartTime = selectedDate
      .clone()
      .hour(startTimeInUserTZ.hour())
      .minute(startTimeInUserTZ.minute())
      .second(0);
    const dayEndTime = selectedDate
      .clone()
      .hour(endTimeInUserTZ.hour())
      .minute(endTimeInUserTZ.minute())
      .second(0);

    // If end time is at or before start (e.g. midnight = hour 0 wraps to start
    // of selected day), push it to the next day so the range is valid.
    if (dayEndTime.isSameOrBefore(dayStartTime)) {
      dayEndTime.add(1, "day");
    }

    let current = dayStartTime.clone();
    const rangeEnd = dayEndTime.clone();

    while (current.isBefore(rangeEnd)) {
      const slotEnd = current.clone().add(meetingDuration, "minutes");
      if (!slotEnd.isSameOrBefore(rangeEnd)) break;

      let isValidSlot = true;

      // Apply booking rules to individual slot
      if (
        isSlotBlockedByRules(
          current,
          slotEnd,
          schedulingRules,
          existingMeetings,
          availability,
        )
      ) {
        isValidSlot = false;
      }

      // Check past time
      if (isValidSlot) {
        const currentTime = moment().tz(userTimezone);
        const isToday = day.isSame(currentTime, "day");

        if (isToday && current.isSameOrBefore(currentTime)) {
          isValidSlot = false;
        }

        if (isValidSlot) {
          const slotStart = current.clone().tz(userTimezone);
          const slotEndTime = slotStart.clone().add(meetingDuration, "minutes");

          const blocked = originalIsSlotBlocked(
            slotStart,
            slotEndTime,
            availability.blocked_periods || [],
            day,
            userTimezone,
          );

          if (!blocked) {
            return true;
          }
        }
      }

      current.add(30, "minutes");
    }
  }

  return false;
};

export const generateTimeSlots = (
  availability,
  selectedDay,
  duration,
  userTimezone,
  schedulingRules,
  existingMeetings,
) => {
  if (!availability?.times || !selectedDay || !duration) {
    return [];
  }

  const dayName = moment(selectedDay, "DD-MM-YYYY").format("ddd");
  const dayAvailability = availability.times[dayName] || [];

  if (!Array.isArray(dayAvailability) || dayAvailability.length === 0) {
    return [];
  }

  const meetingDuration = parseInt(duration);
  const slots = [];

  dayAvailability.forEach(({ start, end }) => {
    if (!start || !end) return;

    const startTimeUTC = convertFirestoreTimestamp(start);
    const endTimeUTC = convertFirestoreTimestamp(end);

    if (!startTimeUTC || !endTimeUTC) return;

    const startTimeInUserTZ = startTimeUTC.clone().tz(userTimezone);
    const endTimeInUserTZ = endTimeUTC.clone().tz(userTimezone);

    const selectedDate = moment.tz(selectedDay, "DD-MM-YYYY", userTimezone);

    const dayStartTime = selectedDate
      .clone()
      .hour(startTimeInUserTZ.hour())
      .minute(startTimeInUserTZ.minute())
      .second(0)
      .millisecond(0);
    const dayEndTime = selectedDate
      .clone()
      .hour(endTimeInUserTZ.hour())
      .minute(endTimeInUserTZ.minute())
      .second(0)
      .millisecond(0);

    // If end time is at or before start (e.g. midnight = hour 0 wraps to start
    // of selected day), push it to the next day so the range is valid.
    if (dayEndTime.isSameOrBefore(dayStartTime)) {
      dayEndTime.add(1, "day");
    }

    let current = dayStartTime.clone();
    const rangeEnd = dayEndTime.clone();

    while (current.isBefore(rangeEnd)) {
      const slotEnd = current.clone().add(meetingDuration, "minutes");

      if (slotEnd.isSameOrBefore(rangeEnd)) {
        let isValidSlot = true;

        // Apply booking rules
        if (
          isSlotBlockedByRules(
            current,
            slotEnd,
            schedulingRules,
            existingMeetings,
            availability,
          )
        ) {
          isValidSlot = false;
        }

        if (isValidSlot) {
          const slotObject = {
            start: current.clone(),
            end: slotEnd.clone(),
            available: true,
            time: current.format("HH:mm"),
            datetime: current.toISOString(),
            timestamp: current.unix(),
            fullTime: current.clone(),
            timezone: userTimezone,
            displayTime: current.format("h:mm A"),
          };

          slots.push(slotObject);
        }
      }

      current.add(30, "minutes");
    }
  });

  // Filter out past times and blocked periods
  const currentTime = moment().tz(userTimezone);
  const selectedDate = moment.tz(selectedDay, "DD-MM-YYYY", userTimezone);
  const isToday = selectedDate.isSame(currentTime, "day");

  const filteredSlots = slots.filter((slot) => {
    if (isToday && slot.start.isSameOrBefore(currentTime)) {
      return false;
    }

    const slotStart = slot.start.clone().tz(userTimezone);
    const slotEndTime = slotStart.clone().add(meetingDuration, "minutes");

    const blocked = originalIsSlotBlocked(
      slotStart,
      slotEndTime,
      availability.blocked_periods || [],
      selectedDate,
      userTimezone,
    );

    return !blocked;
  });

  return filteredSlots;
};

export const createMeetingsByDateMap = (existingMeetings) => {
  if (!existingMeetings?.length) return {};

  const grouped = {};
  existingMeetings.forEach((meeting) => {
    const timestamp = convertFirestoreTimestamp(meeting.scheduled_date_time);
    if (timestamp) {
      const date = timestamp.format("YYYY-MM-DD");
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(meeting);
    }
  });
  return grouped;
};
